import { q, type Recorder } from './trace';
import type { Grade } from '../model/types';

export interface SummationMember {
  sourceId: string;
  tag: string;
  grade: Grade;
  massRate: number;
  volumetricRate: number;
  /** Release characteristic of this source, m³/s. Summed per B.4 so mixed substances work. */
  qc: number;
  lfl: number;
}

export interface Scenario {
  /** The grade this scenario applies to: continuous = C; primary = C + P; secondary = C + P + largest S. */
  grade: Grade;
  members: string[];
  sumMassRate: number;
  sumVolumetricRate: number;
  sumQc: number;
  lflMin: number;
  /** Background concentration (eq. C.1), vol/vol. */
  xb: number;
  /** Critical concentration, vol/vol. */
  xcrit: number;
  exceeds: boolean;
}

export interface SummationInput {
  members: SummationMember[];
  maxSimultaneousPrimary: number | 'all';
  inefficiencyF: number;
  airFlow: number;
  criticalFraction: number;
}

/** Background concentration in a ventilated room (eq. C.1): Xb = f·Qg / (Qg + Q1). */
export function backgroundConcentration(f: number, qg: number, airFlow: number): number {
  return (f * qg) / (qg + airFlow);
}

/**
 * Summation of releases in an indoor area (B.4).
 * - continuous: all continuous releases
 * - primary: the continuous total plus the primary releases that can occur together
 * - secondary: the primary total plus the single largest secondary release
 */
export function summate(input: SummationInput, rec: Recorder): Partial<Record<Grade, Scenario>> {
  const byQc = (a: SummationMember, b: SummationMember) => b.qc - a.qc;
  const continuous = input.members.filter((m) => m.grade === 'continuous');
  const primaries = input.members.filter((m) => m.grade === 'primary').sort(byQc);
  const secondaries = input.members.filter((m) => m.grade === 'secondary').sort(byQc);
  const n = input.maxSimultaneousPrimary === 'all' ? primaries.length : Math.max(0, Math.floor(input.maxSimultaneousPrimary));
  const chosenPrimaries = primaries.slice(0, n);
  const largestSecondary = secondaries[0];

  const build = (grade: Grade, members: SummationMember[]): Scenario => {
    const sumMassRate = members.reduce((s, m) => s + m.massRate, 0);
    const sumVolumetricRate = members.reduce((s, m) => s + m.volumetricRate, 0);
    const sumQc = members.reduce((s, m) => s + m.qc, 0);
    const lflMin = Math.min(...members.map((m) => m.lfl));
    const xb = backgroundConcentration(input.inefficiencyF, sumVolumetricRate, input.airFlow);
    const xcrit = input.criticalFraction * lflMin;
    const scenario: Scenario = { grade, members: members.map((m) => m.sourceId), sumMassRate, sumVolumetricRate, sumQc, lflMin, xb, xcrit, exceeds: xb > xcrit };
    const label = { continuous: 'continuous', primary: 'primary plus continuous', secondary: 'secondary plus primary plus continuous' }[grade];
    rec.step({
      id: `summation.${grade}`,
      label: `Summed releases: ${label}`,
      ref: { clause: 'B.4' },
      inputs: { 'ΣWg': q(sumMassRate, 'kg/s'), 'ΣQg': q(sumVolumetricRate, 'm3/s'), 'ΣQc': q(sumQc, 'm3/s') },
      output: { symbol: 'members', text: members.map((m) => m.tag).join(', ') },
    });
    rec.step({
      id: `background.${grade}`,
      label: `Background concentration: ${label}`,
      ref: { clause: 'C.3.6.2', eq: 'C.1' },
      inputs: { f: q(input.inefficiencyF, '1'), 'ΣQg': q(sumVolumetricRate, 'm3/s'), Q1: q(input.airFlow, 'm3/s'), Xcrit: q(xcrit, 'vol/vol') },
      output: { symbol: 'Xb', ...q(xb, 'vol/vol') },
      notes: [
        { kind: 'info', text: scenario.exceeds ? 'Xb is above Xcrit: the degree of dilution is low (C.3.5).' : 'Xb is below Xcrit.' },
        ...(members.length > 1 && new Set(members.map((m) => m.lfl)).size > 1
          ? [{ kind: 'decision' as const, text: 'Several substances: Xcrit uses the lowest LFL present (B.4).', ref: { clause: 'B.4' } }]
          : []),
      ],
    });
    return scenario;
  };

  const out: Partial<Record<Grade, Scenario>> = {};
  if (continuous.length) out.continuous = build('continuous', continuous);
  if (chosenPrimaries.length) out.primary = build('primary', [...continuous, ...chosenPrimaries]);
  if (largestSecondary) out.secondary = build('secondary', [...continuous, ...chosenPrimaries, largestSecondary]);
  if (primaries.length > n) {
    rec.issue({ code: 'summation.primary-subset', severity: 'info', message: `${n} of ${primaries.length} primary releases are assumed to occur together. Record the basis for this.`, ref: { clause: 'B.4', md: 'MD-006' } });
  }
  return out;
}
